// frontend/src/components/InsufficientCreditsModal.tsx
//
// Modal shown when a mini-app tries to call the backend but the user
// doesn't have enough credits (less than the average spend for this app).
// For unauthenticated users, also mentions the free 50-credit signup bonus.

import { Link } from 'react-router-dom';
import { Modal } from './Modal';
import { AlertTriangle, Coins, ArrowRight, UserPlus, ShoppingCart } from 'lucide-react';
import { PAYMENTS_DISABLED, PAYMENTS_DISABLED_MESSAGE } from '../lib/payments';

interface InsufficientCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
  isAuthenticated: boolean;
  creditsRemaining?: number;
  avgCreditsNeeded?: number;
}

export function InsufficientCreditsModal({
  isOpen,
  onClose,
  isAuthenticated,
  creditsRemaining,
  avgCreditsNeeded,
}: InsufficientCreditsModalProps) {
  const returnTo = encodeURIComponent(window.location.pathname + window.location.search);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Not Enough Credits" size="sm">
      <div className="text-center">
        <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-theme-error-bg mb-4">
          <AlertTriangle className="h-8 w-8 text-theme-error" />
        </div>

        <p className="text-theme-text-secondary mb-4">
          {avgCreditsNeeded && creditsRemaining !== undefined ? (
            <>
              This app typically uses <strong>~{avgCreditsNeeded} credits</strong> per
              session, but you {isAuthenticated ? 'only have' : 'have'}{' '}
              <strong>{creditsRemaining}</strong> remaining.
            </>
          ) : (
            <>You don't have enough credits to run this app.</>
          )}
        </p>

        {isAuthenticated ? (
          <>
            <div className="bg-theme-surface rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between text-sm mb-2">
                <div className="flex items-center gap-1.5 text-theme-text-muted">
                  <Coins className="h-4 w-4" />
                  Credits remaining
                </div>
                <span className="font-medium text-theme-error">
                  {creditsRemaining ?? 0}
                </span>
              </div>
            </div>

            <div className="space-y-3">
              {PAYMENTS_DISABLED ? (
                <div className="p-3 rounded-lg bg-theme-surface border border-theme-border-muted text-sm text-theme-text-secondary">
                  {PAYMENTS_DISABLED_MESSAGE}
                </div>
              ) : (
                <Link
                  to="/pricing"
                  onClick={onClose}
                  className="btn btn-primary btn-block"
                >
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  Subscribe or Buy Credits
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              )}
              <button onClick={onClose} className="btn btn-secondary btn-block">
                Close
              </button>
            </div>

            {!PAYMENTS_DISABLED && (
              <p className="text-xs text-theme-text-muted mt-4">
                Subscription credits reset monthly. You can also buy pay-as-you-go credit packs.
              </p>
            )}
          </>
        ) : (
          <>
            <div className="bg-theme-surface rounded-lg p-4 mb-6 text-sm text-theme-text-secondary">
              <div className="flex items-center gap-2 mb-2">
                <UserPlus className="h-4 w-4 text-accent-500 shrink-0" />
                <span className="font-medium text-theme-text">
                  Create a free account and get 64 complimentary credits!
                </span>
              </div>
              <p className="text-theme-text-muted ml-6">
                {PAYMENTS_DISABLED
                  ? PAYMENTS_DISABLED_MESSAGE
                  : 'Or subscribe to a paid plan for more credits each month.'}
              </p>
            </div>

            <div className="space-y-3">
              <Link
                to={`/register?returnTo=${returnTo}`}
                onClick={onClose}
                className="btn btn-primary btn-block"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Create Free Account
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
              <Link
                to={`/login?returnTo=${returnTo}`}
                onClick={onClose}
                className="btn btn-secondary btn-block"
              >
                Sign In
              </Link>
              <button onClick={onClose} className="btn btn-secondary btn-block">
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
